import { useEffect, useRef } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { NEXTGEN_CHAT_EVENT, trackNextgenChatEvent } from "./analytics";

// Per-run bookkeeping captured across the AG-UI lifecycle so the terminal event
// can report latency and the tools the run used. Runs are sequential within a
// conversation, so a single slot is enough.
interface RunProgress {
  runId: string;
  startedAt: number;
  toolNames: string[];
}

// Emits the message-level nextgen chat analytics that only the agent run
// lifecycle can source: a successful response (`analysis_agent_message_received`,
// with latency and tool usage) and an agent-side failure
// (`analysis_agent_message_error`). The user-driven counterparts — message sent,
// message cancelled — fire at their handlers in `Chat`, where intent is
// unambiguous.
//
// Mirrors the run model in `useAgent`: `onRunFinishedEvent` is the success path
// and `onRunErrorEvent` a genuine failure. A cancellation ends via its own
// "[cancelled]" marker rather than RUN_ERROR, so it is filtered here the same
// way `useRunError` does; other terminations (transport drops) surface through
// `onRunFinalized` and are intentionally counted as neither received nor errored.
export function useConversationAnalytics(
  agent: AbstractAgent,
  context: {
    conversationId: string;
    projectId?: string;
    agentId?: string | null;
    agentName?: string | null;
  }
): void {
  const { conversationId, projectId, agentId, agentName } = context;
  const runRef = useRef<RunProgress | null>(null);

  useEffect(() => {
    const ctx = { conversationId, projectId, agentId, agentName };
    const sub = agent.subscribe({
      // RunErrorEvent carries no runId, so capture it here to tag the failure.
      onRunStartedEvent: ({ event }) => {
        runRef.current = { runId: event.runId, startedAt: performance.now(), toolNames: [] };
      },
      onToolCallStartEvent: ({ event }) => {
        runRef.current?.toolNames.push(event.toolCallName);
      },
      onRunFinishedEvent: () => {
        const run = runRef.current;
        runRef.current = null;
        trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.MESSAGE_RECEIVED, ctx, {
          trace_id: run?.runId,
          response_time_ms: run ? Math.round(performance.now() - run.startedAt) : undefined,
          tool_calls_count: run?.toolNames.length ?? 0,
          tools_used: run?.toolNames ?? [],
        });
      },
      onRunErrorEvent: ({ event }) => {
        const run = runRef.current;
        runRef.current = null;
        const message = event.message?.trim();
        // A cancel is not a failure — guard defensively so it is never logged
        // as one, matching useRunError's own cancel filter.
        if (message && message.toLowerCase().includes("cancel")) return;
        trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.MESSAGE_ERROR, ctx, {
          trace_id: run?.runId,
          error_message: message || "Unknown error",
        });
      },
    });
    return () => sub.unsubscribe();
  }, [agent, conversationId, projectId, agentId, agentName]);
}
